import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";

import MainTemplate from "@/components/Templates/MainTemplate";
import { FlexDiv } from "@/components/Atoms/Box";
import Button from "@/components/Atoms/Button";
import PartnerForm from "@/components/Organisms/PartnerForm";
import Testimonials from "@/components/Organisms/Testimonials";

import BannerSection from "./BannerSection";
import HowItWorks from "./HowItWorks";
import Symptoms from "./Symptoms";
import CardComponent from "./Cards";
import MeetInstructor from "./MeetInstructors";

import { useAppSelector } from "@/hooks";
import { generateUrlArguments, isMobile, renderMetaTags } from "@/utils";
import { DEFAULT_URL_ARGUMENTS, REFERRAL_ARGUMENTS } from "@/constants";

const YogaPage = ({ slug }) => {
  const {
    data: yogaDetailsData,
    isLoading,
    success,
    error,
  } = useAppSelector((state) => state.yogaProgram);
  const { countryCodeData: countryCodes } = useAppSelector(
    (state) => state.dashboard
  );

  const packageReducerData = {};
  const landinPageReducerData = {};

  const [isBtnVisible, setBtnVisible] = useState(false);

  let programType = slug;

  const cardRef = useRef(null);
  const callBackRef = useRef(null);
  const footerRef = useRef(null);

  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState("");

  const setPayment = (data) => {
    setPaymentData(data);
    setPaymentModal(true);
  };

  useEffect(() => {
    if (isMobile) {
      setBtnVisible(true);
    }
    window.onscroll = () => {
      let callbackButton = document.getElementById("floatingCta");
      let footerPos =
        footerRef.current?.getBoundingClientRect()?.top - window.innerHeight;
      let formPosBottomPos = callbackButton?.getBoundingClientRect()?.bottom;
      let formPosTopPos =
        callbackButton?.getBoundingClientRect()?.top - window.innerHeight;
      formPosTopPos >= 0 || (formPosBottomPos <= 0 && footerPos > 0)
        ? setBtnVisible(true)
        : setBtnVisible(false);
    };
  }, []);

  const handleScrollToSection = () => {
    if (cardRef.current) {
      let cardDiv = cardRef.current;
      cardDiv.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleScrollToCallBackForm = () => {
    let btn = callBackRef.current;
    if (btn) {
      btn.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const metaTags = renderMetaTags(yogaDetailsData);

  let urlParams = "";
  if (typeof window !== "undefined") {
    urlParams = window.location.search;
  }

  let ARGUMENTS = yogaDetailsData?.hero?.accept_referral
    ? urlParams
      ? `${urlParams}${urlParams.endsWith("&") ? "" : "&"}${REFERRAL_ARGUMENTS}`
      : `?${REFERRAL_ARGUMENTS}`
    : urlParams || DEFAULT_URL_ARGUMENTS;

  const urlsArguments = generateUrlArguments(ARGUMENTS);

  return (
    <>
      {metaTags}
      <MainTemplate
        // isLoading={isLoading}
        error={error}
        success={success}
        footerRef={footerRef}
      >
        <BannerSection
          yogaDetailsData={yogaDetailsData}
          handleScrollToSection={handleScrollToSection}
        />
        <HowItWorks
          yogaDetailsData={yogaDetailsData}
          programType={programType}
          countryCodes={countryCodes}
          callBackRef={callBackRef}
          urlsArguments={urlsArguments}
        />
        <Symptoms
          isMobile={isMobile}
          data={yogaDetailsData}
          programType={programType}
          handleScrollToCallBackForm={
            yogaDetailsData?.hero?.accept_referral
              ? handleScrollToSection
              : handleScrollToCallBackForm
          }
        />
        {yogaDetailsData?.all_packages?.length > 0 && (
          <CardComponent
            planData={yogaDetailsData?.all_packages}
            cardRef={cardRef}
            programType={programType}
            openPaymentPage={setPayment}
          />
        )}
        <AnimatePresence>
          {paymentModal && (
            <PartnerForm
              showAppDownLoadButtonLink
              // urlsArguments={urlsArguments}
              showSelectPackage
              defaultSelectedGoal={
                yogaDetailsData?.hero?.hero_signup_primary_pricing?.package_slug
              }
              isRequestCallbackPage={yogaDetailsData?.hero?.request_callback}
              defaultSelectedPack={{
                ...paymentData,
                slug: paymentData.purchased_plan_slug,
              }}
              formTitle="Sign up"
              formSubtitle="Pay now to book your spot - we'll get in touch to set you up"
              needMoreInfo="Need more information? Request a call back"
              isCallbackModal={paymentModal}
              setShowCallbackModal={setPaymentModal}
              urlsArguments={urlsArguments}
            />
          )}
        </AnimatePresence>
        {isBtnVisible &&
          yogaDetailsData?.hero?.hero_cta_title_constant &&
          yogaDetailsData?.hero?.hero_signup_primary_pricing?.price && (
            <FlexDiv>
              <FixedBtn
                variant={"primary"}
                fontFamily={"var(--font-700)"}
                fontSize={"16px"}
                onClick={() => handleScrollToCallBackForm()}
                btnPadding={"11px 50px"}
              >
                {packageReducerData?.promoCodeApplied &&
                landinPageReducerData?.pageData ? (
                  <>
                    {yogaDetailsData?.hero?.hero_cta_title_constant + ` `}
                    <StrikeThroughText>
                      {landinPageReducerData?.pageData?.original_price}
                    </StrikeThroughText>
                    {` ` + landinPageReducerData?.pageData?.new_price}
                  </>
                ) : (
                  `${yogaDetailsData?.hero?.hero_cta_title_constant} ${yogaDetailsData?.hero?.hero_signup_primary_pricing?.price}`
                )}
              </FixedBtn>
            </FlexDiv>
          )}
        <MeetInstructor
          data={yogaDetailsData?.our_instructors}
          isMobile={isMobile}
        />
        {yogaDetailsData?.testimonials?.testimonials.length > 0 && (
          <Testimonials
            data={yogaDetailsData?.testimonials}
            isMobile={isMobile}
          />
        )}
      </MainTemplate>
    </>
  );
};

export default YogaPage;

const FixedBtn = styled(Button)`
  display: none;
  @media only screen and (max-width: 1159px) {
    display: block;
    position: fixed;
    left: 50%;
    white-space: nowrap;
    transform: translateX(-50%);
    z-index: 999;
    bottom: 20px;
  }
`;

const StrikeThroughText = styled.del`
  font-family: inherit;
  font-size: inherit;
  color: inherit;
`;
